import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import * as Sentry from '@sentry/capacitor';

import {
  SENTRY_DIST,
  SENTRY_DSN,
  SENTRY_RELEASE,
  SENTRY_SOURCE,
} from 'config/sentry-constants.cjs';

import type { IMonitoringService } from '../monitoring.impl';
import type {
  MonitoringServiceError,
  MonitoringServiceLevel,
  MonitoringServiceTag,
  MonitoringServiceUserContext,
} from '../types/monitoring.types';

const _isProd = process.env.NODE_ENV === 'production';

let _errorListener: ((event: ErrorEvent) => void) | null = null;
let _unhandledRejectionListener:
  | ((event: PromiseRejectionEvent) => void)
  | null = null;

class SentryMobileAdapter implements IMonitoringService {
  private platform = Capacitor.getPlatform();

  private appVersion =
    this.platform === 'android'
      ? process.env.NEXT_PUBLIC_APP_ANDROID_VERSION_NAME
      : process.env.NEXT_PUBLIC_APP_IOS_VERSION_NAME;

  init(): void {
    const initSentryCapacitor = (options: {
      dsn: string;
      release?: string;
      environment?: string;
      debug?: boolean;
      tracesSampleRate?: number;
    }) => {
      Sentry.init({
        dsn: options.dsn,
        release: options.release ?? SENTRY_RELEASE,
        environment:
          options.environment ?? process.env.NODE_ENV ?? 'development',
        debug: options.debug ?? false,
        tracesSampleRate: options.tracesSampleRate ?? 1.0,
        enableAutoSessionTracking: true,
        dist: SENTRY_DIST,
        beforeSend(event) {
          if (typeof window !== 'undefined') {
            event.contexts = {
              ...event.contexts,
              device: {
                ...event.contexts?.device,
                userAgent: navigator.userAgent,
                viewport: {
                  width: window.innerWidth,
                  height: window.innerHeight,
                },
              },
            };
          }

          return event;
        },
      });
    };

    if (_isProd) {
      initSentryCapacitor({
        dsn: SENTRY_DSN,
        release: SENTRY_RELEASE,
        environment: process.env.NODE_ENV ?? 'development',
        debug: process.env.NODE_ENV === 'development',
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
      });
    }
  }

  addListeners(): void {
    _errorListener = (event: ErrorEvent) => {
      Sentry.captureException(event.error, {
        tags: {
          source: SENTRY_SOURCE,
          platform: 'mobile',
        },
      });
    };

    _unhandledRejectionListener = (event: PromiseRejectionEvent) => {
      Sentry.captureException(event.reason, {
        tags: {
          source: SENTRY_SOURCE,
          platform: 'mobile',
          type: 'unhandled-rejection',
        },
      });
    };

    window.addEventListener('error', _errorListener);
    window.addEventListener('unhandledrejection', _unhandledRejectionListener);
  }

  removeListeners(): void {
    if (_errorListener) {
      window.removeEventListener('error', _errorListener);
      _errorListener = null;
    }

    if (_unhandledRejectionListener) {
      window.removeEventListener(
        'unhandledrejection',
        _unhandledRejectionListener
      );
      _unhandledRejectionListener = null;
    }
  }

  captureError(error: MonitoringServiceError): void {
    Sentry.captureException(error, {
      tags: {
        component: error.componentName,
        source: SENTRY_SOURCE,
        platform: 'mobile',
        type: 'error',
      },
      extra: {
        ...error.error,
        timestamp: new Date().toISOString(),
        ...error.context,
      },
    });
  }

  captureMessage(message: string, level?: MonitoringServiceLevel): void {
    Sentry.captureMessage(message, {
      level: level ?? 'info',
      tags: {
        source: SENTRY_SOURCE,
        platform: 'mobile',
        type: 'info',
        os: Capacitor.getPlatform(),
      },
    });
  }

  setUserContext(userContext: MonitoringServiceUserContext): void {
    Sentry.setUser({
      id: userContext.userInforId,
      username: userContext.nome,
      autoescolaId: userContext.autoescolaId.toString(),
      unidadeId: userContext.unidadeId.toString(),
    });
  }

  clearUserContext(): void {
    Sentry.setUser(null);
  }

  setTag(tag: MonitoringServiceTag): void {
    Sentry.setTag(tag.key, tag.value);
  }

  addBaseTags(dataUser: MonitoringServiceUserContext): void {
    Sentry.setTags({
      os: Capacitor.getPlatform(),
      userInforId: dataUser?.userInforId ?? '',
      nome: dataUser?.nome ?? '',
      autoescolaId: dataUser?.autoescolaId.toString() ?? '',
      unidadeId: dataUser?.unidadeId.toString() ?? '',
      appVersion: this.appVersion,
    });
    void Device.getInfo().then((deviceInfo) => {
      Sentry.setTags({
        userInforId: dataUser?.userInforId ?? '',
        nome: dataUser?.nome ?? '',
        autoescolaId: dataUser?.autoescolaId.toString() ?? '',
        unidadeId: dataUser?.unidadeId.toString() ?? '',
        deviceModel: deviceInfo.model,
        devicePlatform: deviceInfo.platform,
        deviceVersion: deviceInfo.osVersion,
        appVersion: this.appVersion,
      });
    });
  }
}

export default SentryMobileAdapter;
